import { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plug,
  RefreshCw,
  Trash2,
  Plus,
  CheckCircle,
  AlertTriangle,
  Loader2,
  History,
  X,
  CalendarClock,
  RotateCcw,
} from "lucide-react";
import toast from "react-hot-toast";
import Tooltip from "../../widgets/ToolTip";
import folderConnectionService, {
  FolderConnection,
  FolderConnectionSource,
  FolderConnectionLog,
} from "../../services/folderConnection.service";
import apiEndpointService from "../../services/apiEndpoint.service";
import databaseConfigService from "../../services/databaseConfig.service";
import ApiEndpoint from "../apiEndponits/ApiEndpoint";
import DatabaseConfig from "../databaseConfig/DatabaseConfig";
import apiSvg from "../../assets/svg/api-interface-svgrepo-com.svg";
import databaseSvg from "../../assets/svg/database-migration-service-svgrepo-com.svg";
import { CustomSelect } from "../../workflow/common/node-ui";

const AI_LEVELS = ["LOW", "MEDIUM", "HIGH"];
const PROCESSING_TYPES = ["AI_OCR", "NORMAL_OCR"];

const CRON_PRESETS: { label: string; value: string }[] = [
  { label: "Every hour", value: "0 * * * *" },
  { label: "Every 6 hours", value: "0 */6 * * *" },
  { label: "Every day at 6 AM", value: "0 6 * * *" },
  { label: "Every Monday at 6 AM", value: "0 6 * * 1" },
  { label: "Custom…", value: "custom" },
];

const cronLabel = (cron?: string | null) => {
  if (!cron) return "";
  const found = CRON_PRESETS.find((p) => p.value === cron);
  return found ? found.label : cron;
};

type Tab = "sources" | "api" | "database";

const timeAgo = (dateString?: string | null) => {
  if (!dateString) return null;
  const diffMs = Date.now() - new Date(dateString).getTime();
  if (isNaN(diffMs) || diffMs < 0) return null;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const formatWhen = (d?: string) =>
  d
    ? new Date(d).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const SourceIcon = ({
  type,
  className = "h-4 w-4",
}: {
  type: FolderConnectionSource;
  className?: string;
}) => (
  <img
    src={type === "API" ? apiSvg : databaseSvg}
    alt={type === "API" ? "API integration" : "Database"}
    className={`${className} object-contain`}
  />
);

const FolderConnectionsPage = () => {
  const { documentStoreId } = useParams<{ documentStoreId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get("folderId") || null;
  const folderName = searchParams.get("folderName") || null;

  const [tab, setTab] = useState<Tab>("sources");

  // connected sources
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState<FolderConnection[]>([]);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [logsFor, setLogsFor] = useState<string | null>(null);
  const [logs, setLogs] = useState<FolderConnectionLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // auto-sync schedule editor
  const [schedFor, setSchedFor] = useState<string | null>(null);
  const [schedCronChoice, setSchedCronChoice] = useState("0 6 * * *");
  const [schedCustomCron, setSchedCustomCron] = useState("");
  const [savingSched, setSavingSched] = useState(false);

  // connect panel
  const [connectOpen, setConnectOpen] = useState(false);
  const [sourceType, setSourceType] = useState<FolderConnectionSource>("API");
  const [configs, setConfigs] = useState<any[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [aiLevel, setAiLevel] = useState("MEDIUM");
  const [processingType, setProcessingType] = useState("AI_OCR");
  const [connecting, setConnecting] = useState(false);

  const scopeLabel = folderId
    ? folderName
      ? `Folder: ${folderName}`
      : "this folder"
    : "Store root";

  const loadConnections = async () => {
    if (!documentStoreId) return;
    setLoading(true);
    try {
      setConnections(
        await folderConnectionService.list(documentStoreId, folderId),
      );
    } catch {
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentStoreId, folderId]);

  const fetchConfigs = async () => {
    setLoadingConfigs(true);
    setSelectedConfigId("");
    try {
      if (sourceType === "API") {
        const res = await apiEndpointService.getApiEndpoints({
          skip: 0,
          limit: 100,
        });
        setConfigs(res.data?.configs || []);
      } else {
        const res = await databaseConfigService.getDatabaseConfigs({
          skip: 0,
          limit: 100,
        });
        setConfigs(res.data?.databaseConfigList || []);
      }
    } catch {
      setConfigs([]);
    } finally {
      setLoadingConfigs(false);
    }
  };

  useEffect(() => {
    if (!connectOpen) return;
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOpen, sourceType]);

  const handleConnect = async () => {
    if (!selectedConfigId || !documentStoreId) return;
    setConnecting(true);
    try {
      await folderConnectionService.connect({
        document_store_id: documentStoreId,
        folder_id: folderId,
        source_type: sourceType,
        config_id: selectedConfigId,
        ai_performance_level: aiLevel,
        file_processing_type: processingType,
      });
      toast.success("Source connected");
      setConnectOpen(false);
      await loadConnections();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.error_description || "Failed to connect",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (conn: FolderConnection, force = false) => {
    setSyncingId(conn._id);
    try {
      const result = await folderConnectionService.sync(conn._id, force);
      const skipped = result.skipped || 0;
      toast.success(
        skipped > 0
          ? `Imported ${result.imported} new file${result.imported === 1 ? "" : "s"}, skipped ${skipped} unchanged`
          : `Imported ${result.imported} file${result.imported === 1 ? "" : "s"}`,
      );
      await loadConnections();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.error_description || "Sync failed",
      );
      await loadConnections();
    } finally {
      setSyncingId(null);
    }
  };

  const handleRemove = async (conn: FolderConnection) => {
    try {
      await folderConnectionService.remove(conn._id);
      toast.success("Connection removed");
      await loadConnections();
    } catch {
      toast.error("Failed to remove connection");
    }
  };

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const handleToggleActive = async (conn: FolderConnection) => {
    const next = conn.is_active === false; // currently inactive → activate
    setTogglingId(conn._id);
    try {
      await folderConnectionService.setActive(conn._id, next);
      toast.success(next ? "Connection activated" : "Connection paused");
      await loadConnections();
    } catch {
      toast.error("Failed to update connection");
    } finally {
      setTogglingId(null);
    }
  };

  const toggleLogs = async (conn: FolderConnection) => {
    if (logsFor === conn._id) {
      setLogsFor(null);
      return;
    }
    setLogsFor(conn._id);
    setLoadingLogs(true);
    try {
      setLogs(await folderConnectionService.logs(conn._id));
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const openSchedule = (conn: FolderConnection) => {
    if (schedFor === conn._id) {
      setSchedFor(null);
      return;
    }
    setSchedFor(conn._id);
    const cron = conn.cron_expression || "0 6 * * *";
    const isPreset = CRON_PRESETS.some(
      (p) => p.value === cron && p.value !== "custom",
    );
    setSchedCronChoice(isPreset ? cron : "custom");
    setSchedCustomCron(isPreset ? "" : cron);
  };

  const effectiveCron = () =>
    schedCronChoice === "custom" ? schedCustomCron.trim() : schedCronChoice;

  const saveAutoSync = async (conn: FolderConnection, enabled: boolean) => {
    setSavingSched(true);
    try {
      await folderConnectionService.setAutoSync(conn._id, {
        enabled,
        cron_expression: enabled ? effectiveCron() : undefined,
      });
      toast.success(enabled ? "Auto-sync enabled" : "Auto-sync disabled");
      setSchedFor(null);
      await loadConnections();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.error_description ||
          "Failed to update auto-sync",
      );
    } finally {
      setSavingSched(false);
    }
  };

  /* ---------------- inline editors (used inside expandable rows) ---------------- */
  const scheduleEditor = (conn: FolderConnection) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
        <CalendarClock className="w-4 h-4 text-teal-600" />
        Automatic sync
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Run
          </label>
          <CustomSelect
            value={schedCronChoice}
            onChange={setSchedCronChoice}
            options={CRON_PRESETS.map((p) => ({
              value: p.value,
              label: p.label,
            }))}
          />
        </div>
        {schedCronChoice === "custom" && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Cron expression
            </label>
            <input
              value={schedCustomCron}
              onChange={(e) => setSchedCustomCron(e.target.value)}
              placeholder="e.g. 0 */4 * * *"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        )}
      </div>
      <p className="text-[11px] text-gray-400">
        Timing is handled by the scheduler service (timezone Asia/Kolkata).
      </p>
      <div className="flex items-center gap-3 max-w-2xl justify-between">
        {conn.auto_sync ? (
          <button
            type="button"
            onClick={() => saveAutoSync(conn, false)}
            disabled={savingSched}
            className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            Turn off auto-sync
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => saveAutoSync(conn, true)}
          disabled={
            savingSched ||
            (schedCronChoice === "custom" && !schedCustomCron.trim())
          }
          className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
        >
          {savingSched && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {conn.auto_sync ? "Update schedule" : "Enable auto-sync"}
        </button>
      </div>
    </div>
  );

  const logsList = () =>
    loadingLogs ? (
      <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…
      </div>
    ) : logs.length === 0 ? (
      <p className="text-xs text-gray-500 py-1">No sync runs yet</p>
    ) : (
      <div className="space-y-2 max-w-3xl">
        {logs.map((log) => {
          const ok = log.status === "success";
          const files = log.file_names || [];
          return (
            <div
              key={log._id}
              className={`flex items-start gap-2 text-xs rounded-md border p-2 ${
                ok
                  ? "border-green-100 bg-green-50/40"
                  : "border-red-100 bg-red-50/40"
              }`}
            >
              {ok ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-2">
                  <span
                    className={`font-medium ${ok ? "text-green-800" : "text-red-800"}`}
                  >
                    {ok
                      ? `Imported ${log.imported_count} new file${log.imported_count === 1 ? "" : "s"}`
                      : "Sync failed"}
                  </span>
                  <span className="text-gray-400 flex-shrink-0">
                    {formatWhen(log.createdAt)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400">
                  <span
                    className={`px-1.5 py-0.5 rounded-full font-medium ${
                      log.triggered_by === "auto"
                        ? "bg-teal-100 text-teal-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {log.triggered_by === "auto" ? "Auto" : "Manual"}
                  </span>
                  {ok && !!log.skipped_count && log.skipped_count > 0 && (
                    <span className="text-gray-500">
                      {log.skipped_count} unchanged skipped
                    </span>
                  )}
                  {typeof log.duration_ms === "number" && (
                    <span>
                      {log.duration_ms < 1000
                        ? `${log.duration_ms} ms`
                        : `${(log.duration_ms / 1000).toFixed(1)} s`}
                    </span>
                  )}
                </div>
                {!ok && log.error_detail && (
                  <p className="text-red-700 mt-1 break-words">
                    {log.error_detail}
                  </p>
                )}
                {ok && files.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {files.slice(0, 6).map((name, i) => (
                      <span
                        key={i}
                        className="inline-block max-w-[160px] truncate text-[11px] text-gray-600 bg-white border border-gray-200 px-1.5 py-0.5 rounded"
                        title={name}
                      >
                        {name}
                      </span>
                    ))}
                    {files.length > 6 && (
                      <span className="text-[11px] text-gray-400">
                        +{files.length - 6} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );

  /* ---------------- Connected sources tab ---------------- */
  const renderSources = () => (
    <div className="w-full space-y-3">
      {/* Header card */}
      <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Plug className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Connected sources
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              APIs / databases that import files into{" "}
              {folderId ? "this folder" : "the store root"}
            </p>
          </div>
        </div>
        {!connectOpen && (
          <button
            onClick={() => setConnectOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 active:scale-95 transition-all flex-shrink-0"
            type="button"
          >
            <Plus className="w-3.5 h-3.5" />
            Connect a source
          </button>
        )}
      </div>

      {/* Connect panel */}
      {connectOpen && (
        <div className="border border-gray-200 rounded-xl bg-white p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Connect a source
            </h3>
            <button
              onClick={() => setConnectOpen(false)}
              className="text-gray-400 hover:text-gray-600"
              type="button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {(["API", "DATABASE"] as FolderConnectionSource[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setSourceType(t)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  sourceType === t
                    ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <SourceIcon type={t} />
                {t === "API" ? "API Integration" : "Database"}
              </button>
            ))}
          </div>

          {loadingConfigs ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : configs.length === 0 ? (
            <div className="text-xs text-gray-500 border border-dashed border-gray-200 rounded-md px-3 py-3">
              No {sourceType === "API" ? "API endpoints" : "databases"} yet —
              create one in the{" "}
              <button
                type="button"
                className="text-blue-600 hover:underline font-medium"
                onClick={() => {
                  setConnectOpen(false);
                  setTab(sourceType === "API" ? "api" : "database");
                }}
              >
                {sourceType === "API" ? "API endpoints" : "Databases"} tab
              </button>
              .
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto custom-scrollbar space-y-1.5">
              {configs.map((cfg: any) => {
                const id = cfg._id;
                const name =
                  sourceType === "API" ? cfg.api_name : cfg.config_name;
                const detail =
                  sourceType === "API"
                    ? `${cfg.method || "GET"} · ${cfg.endpoint}`
                    : `${cfg.database_name || ""}${cfg.collection_name ? " / " + cfg.collection_name : ""}`;
                const selected = selectedConfigId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelectedConfigId(id)}
                    className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md border transition-all ${
                      selected
                        ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {name}
                      </span>
                      <span className="block text-xs text-gray-500 font-mono truncate">
                        {detail}
                      </span>
                    </span>
                    <span
                      className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                        selected
                          ? "border-blue-600 bg-blue-600"
                          : "border-gray-300"
                      }`}
                    >
                      {selected && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 max-w-lg">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                AI Performance
              </label>
              <CustomSelect
                value={aiLevel}
                onChange={setAiLevel}
                options={AI_LEVELS.map((l) => ({ value: l, label: l }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Processing Type
              </label>
              <CustomSelect
                value={processingType}
                onChange={setProcessingType}
                options={PROCESSING_TYPES.map((t) => ({ value: t, label: t }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setConnectOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConnect}
              disabled={!selectedConfigId || connecting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
              Connect
            </button>
          </div>
        </div>
      )}

      {/* Table card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Source
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Type
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Last sync
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Auto-sync
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                </td>
              </tr>
            ) : connections.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center">
                  <Plug className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">
                    No sources connected yet
                  </p>
                  <button
                    onClick={() => setConnectOpen(true)}
                    className="mt-2 text-xs text-teal-600 hover:underline"
                    type="button"
                  >
                    Connect your first source
                  </button>
                </td>
              </tr>
            ) : (
              connections.map((conn) => {
                const synced = timeAgo(conn.last_synced_at);
                const inactive = conn.is_active === false;
                return (
                  <Fragment key={conn._id}>
                    <tr
                      className={`hover:bg-gray-50 transition-colors ${inactive ? "opacity-60" : ""}`}
                    >
                      {/* Source */}
                      <td className="px-4 py-2.5 max-w-[280px]">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <SourceIcon
                              type={conn.source_type}
                              className="h-4 w-4"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-900 font-medium truncate">
                                {conn.config_name || "(removed config)"}
                              </span>
                              {conn.config_missing && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                                  missing
                                </span>
                              )}
                            </div>
                            <span className="text-gray-400 text-xs font-mono truncate block">
                              {conn.config_detail || conn.source_type}
                            </span>
                          </div>
                        </div>
                      </td>
                      {/* Type */}
                      <td className="px-4 py-2.5 text-gray-600">
                        {conn.source_type === "API" ? "API" : "Database"}
                      </td>
                      {/* Status (toggle) */}
                      <td className="px-4 py-2.5">
                        <Tooltip
                          content={
                            inactive
                              ? "Activate (resume syncing)"
                              : "Pause (block syncs)"
                          }
                        >
                          <button
                            onClick={() => handleToggleActive(conn)}
                            disabled={togglingId === conn._id}
                            type="button"
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                              inactive
                                ? "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200"
                                : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                            }`}
                          >
                            {togglingId === conn._id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${inactive ? "bg-gray-400" : "bg-green-500"}`}
                              />
                            )}
                            {inactive ? "Inactive" : "Active"}
                          </button>
                        </Tooltip>
                      </td>
                      {/* Last sync */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {conn.last_sync_status === "failed" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600">
                            <AlertTriangle className="w-3 h-3" /> Failed
                          </span>
                        ) : synced ? (
                          <span className="text-xs text-gray-500">
                            {synced} · {conn.last_sync_count ?? 0} files
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Never</span>
                        )}
                      </td>
                      {/* Auto-sync */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {conn.auto_sync ? (
                          <span className="inline-flex items-center gap-1 text-xs text-teal-600">
                            <CalendarClock className="w-3 h-3" />{" "}
                            {cronLabel(conn.cron_expression)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip
                            content={inactive ? "Activate to sync" : "Sync now"}
                          >
                            <button
                              onClick={() => handleSync(conn)}
                              disabled={
                                !!syncingId || conn.config_missing || inactive
                              }
                              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 transition-colors"
                              type="button"
                            >
                              {syncingId === conn._id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3.5 h-3.5" />
                              )}
                              Sync
                            </button>
                          </Tooltip>
                          <Tooltip content="Reprocess all (ignore what's already imported)">
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Reprocess all items? This re-imports and re-runs AI on everything from this source, including data already processed.",
                                  )
                                ) {
                                  handleSync(conn, true);
                                }
                              }}
                              disabled={
                                !!syncingId || conn.config_missing || inactive
                              }
                              className="p-1.5 text-gray-400 hover:text-gray-700 rounded disabled:opacity-50"
                              type="button"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Auto-sync schedule">
                            <button
                              onClick={() => openSchedule(conn)}
                              disabled={conn.config_missing}
                              className={`p-1.5 rounded disabled:opacity-50 ${
                                schedFor === conn._id || conn.auto_sync
                                  ? "text-teal-600 bg-teal-50"
                                  : "text-gray-400 hover:text-gray-700"
                              }`}
                              type="button"
                            >
                              <CalendarClock className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Sync history">
                            <button
                              onClick={() => toggleLogs(conn)}
                              className={`p-1.5 rounded ${logsFor === conn._id ? "text-blue-600 bg-blue-50" : "text-gray-400 hover:text-gray-700"}`}
                              type="button"
                            >
                              <History className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Remove connection">
                            <button
                              onClick={() => handleRemove(conn)}
                              disabled={!!syncingId}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded disabled:opacity-50"
                              type="button"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>

                    {schedFor === conn._id && (
                      <tr className="bg-teal-50/40">
                        <td
                          colSpan={6}
                          className="px-4 py-3 border-t border-gray-100"
                        >
                          {scheduleEditor(conn)}
                        </td>
                      </tr>
                    )}
                    {logsFor === conn._id && (
                      <tr className="bg-gray-50/50">
                        <td
                          colSpan={6}
                          className="px-4 py-3 border-t border-gray-100"
                        >
                          {logsList()}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: "sources", label: "Connected sources" },
    { key: "api", label: "API endpoints" },
    { key: "database", label: "Databases" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <button
            onClick={() => navigate(`/files/${documentStoreId}`)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-2"
            type="button"
          >
            <ArrowLeft className="w-4 h-4" /> Back to files
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100">
              <Plug className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Connections</h1>
              <p className="text-xs text-gray-500">{scopeLabel}</p>
            </div>
          </div>
        </div>
        {/* Tabs */}
        <div className="px-6 flex gap-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                // Close the connect panel when leaving — so returning to
                // "Connected sources" shows the list, not an auto-fetching form.
                if (t.key !== "sources") setConnectOpen(false);
                setTab(t.key);
              }}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-teal-600 text-teal-700"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className=" py-6">
        {tab === "sources" && renderSources()}
        {tab === "api" && <ApiEndpoint />}
        {tab === "database" && <DatabaseConfig />}
      </div>
    </div>
  );
};

export default FolderConnectionsPage;
